import { useDispatch } from 'react-redux';
import { setSelectedClient } from '../../../features/client/clientSlice';
import { setTypeOfShowModalType } from '../../../features/modal/modalSlice';
import { MdiUser } from '../../../assets/icons/Icons';
import { formatChapels } from '../../../utils/chapelsFormater';
import { formatDateRange, formatTimeRange } from '../../../utils/formatSchedule';
import { useEffect, useMemo, useState } from 'react';
import { ClientData } from '../../../features/client/clientResponse';

interface ClientItemProps {
    client: ClientData;
}

export default function ClientItem({ client }: ClientItemProps) {
    
    const dispatch = useDispatch()
    const scheduleFrom = client.scheduleFrom;
    const scheduleTo = client.scheduleTo;
    const packages = client.packages ?? []
    const [showAllPackages, setShowAllPackages] = useState<boolean>(false);
    const [expired, setExpired] = useState(
        new Date() > new Date(scheduleTo)
    );

    useEffect(() => {
        const end = new Date(scheduleTo).getTime();
        const remaining = end - Date.now();

        if (remaining <= 0) {
            setExpired(true);
            return;
        }

        const timeout = setTimeout(() => {
            setExpired(true);
        }, remaining);

        return () => clearTimeout(timeout);
}, [scheduleTo]);

    const dateRange = formatDateRange(scheduleFrom, scheduleTo);
    const timeRange  = formatTimeRange(scheduleFrom, scheduleTo);

    const sortedPackages = useMemo(() => {
        const order: Record<string, number> = {
            'Cup-Based': 0,
            'Amount-Based': 1,
        };

        return [...packages].sort(
            (a, b) =>
                (order[a.packageType] ?? Number.MAX_SAFE_INTEGER) -
                (order[b.packageType] ?? Number.MAX_SAFE_INTEGER)
        );
    }, [packages]);

    const displayedPackages = showAllPackages ? sortedPackages : sortedPackages.slice(0, 2);
    const borderColor = expired ? "border-cbColor" : "border-green-500"
    
    const handleClick = () => {
        dispatch(setSelectedClient(client));
        // setUrlQRCode(client.qrCodeBase64);
        dispatch(setTypeOfShowModalType('viewClient'))
    }
    
    return (
        <div 
            key={client.clientID} 
            className={`rounded border-t-8 ${borderColor} cursor-pointer`}
            onClick={handleClick}
        >
            <div className='border border-gray-200 flex flex-col p-2.5 space-y-1 rounded-b bg-gray-100 h-full'>
                <div className="grid grid-cols-6 gap-1">
                    <div className='col-span-5 flex flex-col'>
                        <p title={client.deceasedName}  className={`text-sm font-semibold uppercase truncate`}>{client.deceasedName}</p>
                        <p title={client.chapels ? formatChapels(client.chapels) : ""} className="text-xs font-medium text-gray-800 truncate">
                            {client.chapels && formatChapels(client.chapels)}
                        </p>
                    </div>
                    <div className='col-span-1'>
                        <MdiUser color='black' height={25} width={25}/> 
                    </div>
                </div>
                <hr className="border-gray-300 mb-2" />
                <div className="grid grid-cols-2 gap-1">
                    <div>
                        <p className="text-xs font-medium text-gray-500 uppercase">DATE</p>
                        <p className="text-xs font-medium text-gray-800">{dateRange}</p>
                    </div>
                    <div>
                        <p className="text-xs font-medium text-gray-500">TIME</p>
                        <p className="text-xs font-medium text-gray-800">{timeRange}</p>
                    </div>
                </div>
                <div className='overflow-hidden'>
                    <p className="text-xs font-medium text-gray-500">PACKAGE/S:</p>
                    <div className="grid grid-cols-1 gap-0">
                        {displayedPackages.map((pack, i) => {
                            // const isAmountBase = pack.packageType == 'Amount-Based';
                            const isCupBase = pack.packageType == 'Cup-Based';
                            return (
                                <div key={i} className='overflow-hidden rounded flex justify-between py-0.5'>
                                    <div className="text-xs font-medium text-gray-700 truncate">{pack.packageName}</div>
                                    <div className="text-xs font-medium text-gray-700 truncate">{`${isCupBase ? pack.remainingQty : ''}`}</div>
                                </div>
                            )
                        })}
                        {sortedPackages.length > 2 && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation(); // Prevent the card click
                                    setShowAllPackages((prev) => !prev);
                                }}
                                className="text-xs text-blue-600 font-medium text-left hover:underline mt-1"
                            >
                                {showAllPackages
                                    ? "See less"
                                    : `See ${sortedPackages.length - 2} more...`}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
